import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';
import { useAuthStore } from '../stores/authStore';
import Avatar from './Avatar';
import NotificationBell from './NotificationBell';
import CertificationSelector from './CertificationSelector';
import LanguageSwitcher from './LanguageSwitcher';

/* ── SVG Icons (inline to avoid extra deps) ─────────────────────────────── */
function SunIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 7.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

const navLinks = [
  { to: '/dashboard', labelKey: 'nav.dashboard' },
  { to: '/study', labelKey: 'nav.study' },
  { to: '/exam', labelKey: 'nav.exam' },
  { to: '/review', labelKey: 'nav.review' },
] as const;

const adminLink = { to: '/admin', labelKey: 'nav.admin' } as const;

export default function Header() {
  const { t } = useTranslation();
  const { isDark, toggle } = useTheme();
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /* Close dropdown when clicking outside */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* Close dropdown on Escape key */
  useEffect(() => {
    if (!menuOpen && !mobileNavOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setMobileNavOpen(false);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen, mobileNavOpen]);

  /* Lock body scroll when mobile nav is open */
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileNavOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    setMobileNavOpen(false);
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    setMobileNavOpen(false);
  };

  const displayName = user?.display_name || user?.username || t('common.username');
  const isPremium = !!user?.plan && user.plan !== 'free';

  const navLinkClasses = (isActive: boolean) =>
    `rounded-xl px-3 py-2 text-sm font-medium transition-colors
    ${isActive
      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-[#94A3B8] dark:hover:text-white dark:hover:bg-white/5'
    }`;

  const mobileNavLinkClasses = (isActive: boolean) =>
    `flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium transition-colors
    ${isActive
      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
      : 'text-slate-700 hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:text-white dark:hover:bg-white/5'
    }`;

  return (
    <>
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-lg dark:border-white/10 dark:bg-canvas-dark-2/90 relative">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* ── Brand — Variant B monogram (navy tile + amber bolt) ────── */}
        <NavLink to="/dashboard" className="flex items-center gap-2.5 text-lg font-extrabold tracking-tight flex-shrink-0">
          <svg width="34" height="34" viewBox="0 0 32 32" fill="none" aria-hidden="true" className="flex-shrink-0">
            <rect x="0.5" y="0.5" width="31" height="31" rx="8" fill="#16213A" stroke="rgba(255,255,255,.1)" />
            <path d="M9 11.5C9 9.567 10.567 8 12.5 8H22V11H13C12.4477 11 12 11.4477 12 12V20C12 20.5523 12.4477 21 13 21H22V24H12.5C10.567 24 9 22.433 9 20.5V11.5Z" fill="#fff" />
            <path d="M19 13L16 18H19L17.5 24L23 16.5H20L22 13H19Z" fill="#F59E0B" />
          </svg>
          <span className="hidden sm:inline">
            <span className="text-slate-900 dark:text-white">Cloud</span><span className="text-blue-600 dark:text-blue-400">Master</span>
          </span>
          <span className="sm:hidden text-slate-900 dark:text-white">C<span className="text-blue-600 dark:text-blue-400">M</span></span>
        </NavLink>

        {/* ── Certification selector ──────────────────────────────── */}
        <CertificationSelector />

        {/* ── Desktop navigation (hidden on mobile) ───────────────── */}
        <nav className="hidden md:flex items-center gap-1" aria-label={t('header.mainNavigation')}>
          {navLinks.map(({ to, labelKey }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/dashboard'}
              className={({ isActive }) => navLinkClasses(isActive)}
            >
              {t(labelKey)}
            </NavLink>
          ))}

          {(user?.staff_role || user?.is_admin) && (
            <NavLink
              to={adminLink.to}
              className={({ isActive }) =>
                `rounded-xl px-3 py-2 text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white'
                }`
              }
            >
              {t(adminLink.labelKey)}
            </NavLink>
          )}
        </nav>

        {/* ── Right actions ───────────────────────────────────────── */}
        <div className="flex items-center gap-1">
          {/* Theme toggle */}
          <button
            onClick={toggle}
            className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white"
            aria-label={isDark ? t('header.switchToLight') : t('header.switchToDark')}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Language switcher (desktop only — mobile lives inside the slide-over) */}
          <div className="hidden md:block">
            <LanguageSwitcher variant="desktop" />
          </div>

          {/* Notification bell */}
          {isAuthenticated && <NotificationBell />}

          {/* User dropdown (desktop) */}
          {isAuthenticated && user && (
            <div className="relative hidden md:block" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/5"
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                <Avatar size="sm" src={user.avatar_url} name={displayName} />
                <span className="hidden lg:inline max-w-[100px] truncate">{displayName}</span>
                <ChevronIcon open={menuOpen} />
              </button>

              {/* Dropdown */}
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 rounded-2xl bg-white dark:bg-card-dark border border-slate-200 dark:border-white/10 shadow-[0_10px_40px_rgba(15,23,42,0.12)] overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150" role="menu">
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{displayName}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      {isPremium ? (
                        <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-400">
                          {t('header.premiumBadge')}
                        </span>
                      ) : (
                        <Link to="/pricing" className="inline-flex items-center rounded-full bg-slate-100 dark:bg-white/10 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:text-slate-400 hover:bg-primary/10 hover:text-primary transition-colors" onClick={() => setMenuOpen(false)}>
                          {t('header.freeUpgrade')}
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="py-1.5">
                    <button
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
                      {t('header.viewProfile')}
                    </button>
                    <button
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); navigate('/billing'); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
                      {t('header.billing')}
                    </button>
                    <button
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                      {t('header.changePassword')}
                    </button>
                  </div>
                  <div className="border-t border-slate-200 dark:border-white/10 py-1.5">
                    <button
                      role="menuitem"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" /></svg>
                      {t('header.signOut')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mobile: avatar (tap opens mobile nav) */}
          {isAuthenticated && user && (
            <button
              className="md:hidden ml-1"
              onClick={() => setMobileNavOpen(true)}
              aria-label={t('common.openMenu')}
            >
              <Avatar size="sm" src={user.avatar_url} name={displayName} />
            </button>
          )}

          {/* Hamburger (mobile, when not logged in) */}
          {!isAuthenticated && (
            <button
              className="md:hidden ml-1 rounded-xl p-2 text-slate-600 hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white"
              onClick={() => setMobileNavOpen(true)}
              aria-label={t('common.openMenu')}
            >
              <HamburgerIcon />
            </button>
          )}
        </div>
      </div>

      {/* Provider accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[2px]"
        style={{
          background: `linear-gradient(90deg, transparent 0%, var(--color-provider) 15%, var(--color-provider) 85%, transparent 100%)`,
        }}
        aria-hidden="true"
      />
    </header>

    {/* ── Mobile slide-over nav (portaled to body to escape stacking context) ── */}
    {mobileNavOpen && createPortal(
      <div className="md:hidden" style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileNavOpen(false)}
        />

        {/* Panel */}
        <div className="absolute inset-y-0 right-0 w-72 bg-white dark:bg-card-dark border-l border-slate-200 dark:border-white/10 shadow-2xl overflow-y-auto rounded-l-2xl">
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200 dark:border-white/10">
            {isAuthenticated && user ? (
              <div className="flex items-center gap-3 min-w-0">
                <Avatar size="sm" src={user.avatar_url} name={displayName} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{displayName}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    {isPremium ? (
                      <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-400">
                        {t('header.premiumBadge')}
                      </span>
                    ) : (
                      <Link to="/pricing" className="inline-flex items-center rounded-full bg-slate-100 dark:bg-white/10 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:text-slate-400 hover:bg-primary/10 hover:text-primary transition-colors" onClick={handleNavClick}>
                        {t('header.freeUpgrade')}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{t('header.menu')}</span>
            )}
            <button
              onClick={() => setMobileNavOpen(false)}
              className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white"
              aria-label={t('common.closeMenu')}
            >
              <CloseIcon />
            </button>
          </div>

          {/* Nav links */}
          <nav className="px-3 py-4 space-y-1" aria-label={t('header.mobileNavigation')}>
            {navLinks.map(({ to, labelKey }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/dashboard'}
                onClick={handleNavClick}
                className={({ isActive }) => mobileNavLinkClasses(isActive)}
              >
                {t(labelKey)}
              </NavLink>
            ))}

            {!isPremium && (
              <NavLink
                to="/pricing"
                onClick={handleNavClick}
                className={({ isActive }) => mobileNavLinkClasses(isActive)}
              >
                {t('nav.pricing')}
              </NavLink>
            )}

            {(user?.staff_role || user?.is_admin) && (
              <NavLink
                to={adminLink.to}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium transition-colors
                  ${isActive
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white'
                  }`
                }
              >
                {t(adminLink.labelKey)}
              </NavLink>
            )}
          </nav>

          {/* Language switcher (mobile) */}
          <div className="border-t border-slate-200 dark:border-white/10 mx-3" />
          <LanguageSwitcher variant="mobile" />

          {/* Divider + profile actions */}
          {isAuthenticated && user && (
            <>
              <div className="border-t border-slate-200 dark:border-white/10 mx-3" />
              <div className="px-3 py-4 space-y-1">
                <button
                  onClick={() => { handleNavClick(); navigate('/profile'); }}
                  className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
                  {t('header.profile')}
                </button>
                <button
                  onClick={() => { handleNavClick(); navigate('/billing'); }}
                  className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
                  {t('header.billing')}
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" /></svg>
                  {t('header.signOut')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>,
      document.body,
    )}
    </>
  );
}
